<template>
  <section class="mt-size-2 px-section-x">
    <div class="container flex gap-2 tablet:gap-4">
      <div v-for="link in links" :key="link.id">
        <a
          :href="link.url"
          class="link is-grey text-size-3 leading-none"
          :title="link.title"
          target="_blank"
          rel="external nofollow"
        >
          <SpriteSymbol :name="link.icon" />
        </a>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { Channel } from '#graphql-operations'
import type { NuxtSvgSpriteSymbol } from '#nuxt-svg-sprite/runtime'

const { activeChannel } = useSiteContext()
const requestUrl = useRequestURL()
const route = useRoute()
const { logo } = useLogo()
const { $texts } = useEasyTexts()

interface SharingService {
  id: string
  title: string
  url: string
  icon: NuxtSvgSpriteSymbol
}

function getShareTitle(serviceName: string) {
  const shareOn = $texts('shareOn', 'Share on @service')
  return shareOn.replace('@service', serviceName)
}

const services = computed<SharingService[]>(() => {
  const SERVICE_WHATSAPP: SharingService = {
    id: 'whatsapp',
    title: getShareTitle('WhatsApp'),
    url: 'https://wa.me/?text=@url',
    icon: 'whatsapp',
  }
  const SERVICE_MESSENGER: SharingService = {
    id: 'messenger',
    title: getShareTitle('Facebook Messenger'),
    url: 'https://www.facebook.com/dialog/send?app_id=5303202981&display=popup&link=@url&redirect_uri=@redirect',
    icon: 'messenger',
  }
  const SERVICE_TWITTER: SharingService = {
    id: 'twitter',
    title: getShareTitle('X (Twitter)'),
    url: 'http://twitter.com/share?url=@url',
    icon: 'twitter',
  }
  const SERVICE_EMAIL: SharingService = {
    id: 'email',
    title: $texts('shareWithEmail', 'Share via email'),
    url: 'mailto:?body=@url',
    icon: 'email',
  }
  const SERVICE_LINKEDIN: SharingService = {
    id: 'linkedin',
    title: getShareTitle('LinkedIn'),
    url: 'https://www.linkedin.com/sharing/share-offsite/?url=@url',
    icon: 'linkedin',
  }

  const SERVICE_FACEBOOK: SharingService = {
    id: 'facebook',
    title: getShareTitle('Facebook'),
    url: 'https://www.facebook.com/sharer/sharer.php?u=@url',
    icon: 'facebook',
  }
  return [
    SERVICE_WHATSAPP,
    SERVICE_MESSENGER,
    activeChannel.value === Channel.business
      ? SERVICE_LINKEDIN
      : SERVICE_FACEBOOK,
    SERVICE_TWITTER,
    SERVICE_EMAIL,
  ]
})

const links = computed(() => {
  return services.value.map((service) => {
    const pageUrl = requestUrl.origin + route.path
    const url = service.url
      .replace('@url', pageUrl)
      .replace('@redirect', pageUrl)

    if (service.id === 'email') {
      return {
        ...service,
        url,
      }
    }
    const params = new URLSearchParams({
      utm_medium: service.id === 'email' ? 'email' : 'social',
      utm_content: logo.value.id || '',
      utm_source: service.id,
      utm_campaign: 'Website Shares',
    })

    return {
      ...service,
      url: `${url}&${params.toString()}`,
    }
  })
})
</script>
